import type {
  AgentDto,
  LlmProviderDto,
  TranscriberResponseDto,
  VoiceDto,
} from '@shared/services/apiService/apiService';

import { formatList } from './formatList';

export type AgentSettingsRef = Pick<
  AgentDto,
  'voiceId' | 'transcriberId' | 'llmProviderId' | 'llmModelId' | 'llmModel' | 'llmCustomModelKey'
>;

export type CompanySettingsLists = {
  voices?: VoiceDto[] | null;
  transcribers?: TranscriberResponseDto[] | null;
  llmProviders?: LlmProviderDto[] | null;
};

export type RemovedAgentSettings = {
  voice: boolean;
  transcriber: boolean;
  llmProvider: boolean;
  llmModel: boolean;
};

export type RemovedAgentSettingName = keyof RemovedAgentSettings;

export const REMOVED_AGENT_SETTING_ORDER: RemovedAgentSettingName[] = [
  'voice',
  'transcriber',
  'llmProvider',
  'llmModel',
];

export const NO_REMOVED_AGENT_SETTINGS: RemovedAgentSettings = {
  voice: false,
  transcriber: false,
  llmProvider: false,
  llmModel: false,
};

const isMissingFromList = (id: string | null | undefined, list?: { id: string }[] | null) =>
  Boolean(id) && Array.isArray(list) && !list.some((item) => item.id === id);

export const isVoiceRemoved = (agent: AgentSettingsRef, voices?: VoiceDto[] | null): boolean =>
  isMissingFromList(agent.voiceId, voices);

export const isTranscriberRemoved = (
  agent: AgentSettingsRef,
  transcribers?: TranscriberResponseDto[] | null,
): boolean => isMissingFromList(agent.transcriberId, transcribers);

export const isLlmProviderRemoved = (
  agent: AgentSettingsRef,
  llmProviders?: LlmProviderDto[] | null,
): boolean => isMissingFromList(agent.llmProviderId, llmProviders);

export const isLlmModelRemoved = (
  agent: AgentSettingsRef,
  llmProviders?: LlmProviderDto[] | null,
): boolean => {
  if (!Array.isArray(llmProviders)) return false;
  if (!agent.llmModelId || agent.llmModel || agent.llmCustomModelKey) return false;

  const provider = llmProviders.find((p) => p.id === agent.llmProviderId);
  if (!provider || provider.allowsCustomModels) return false;

  return true;
};

export const getRemovedAgentSettings = (
  agent: AgentSettingsRef,
  company?: CompanySettingsLists | null,
): RemovedAgentSettings => {
  if (!company) return NO_REMOVED_AGENT_SETTINGS;

  return {
    voice: isVoiceRemoved(agent, company.voices),
    transcriber: isTranscriberRemoved(agent, company.transcribers),
    llmProvider: isLlmProviderRemoved(agent, company.llmProviders),
    llmModel: isLlmModelRemoved(agent, company.llmProviders),
  };
};

export type AgentSettingsSelection = {
  voiceId?: string | null;
  transcriberId?: string | null;
  llmProviderId?: string | null;
  llmModelId?: string | null;
};

export type AgentSettingsSelectionLists = CompanySettingsLists & {
  llmModels?: { id: string }[] | null;
};

export const getUnavailableSelections = (
  selection: AgentSettingsSelection,
  lists: AgentSettingsSelectionLists,
): RemovedAgentSettings => {
  const llmProvider = isMissingFromList(selection.llmProviderId, lists.llmProviders);

  return {
    voice: isMissingFromList(selection.voiceId, lists.voices),
    transcriber: isMissingFromList(selection.transcriberId, lists.transcribers),
    llmProvider,
    llmModel: !llmProvider && isMissingFromList(selection.llmModelId, lists.llmModels),
  };
};

export const hasRemovedAgentSettings = (removed: RemovedAgentSettings): boolean =>
  REMOVED_AGENT_SETTING_ORDER.some((name) => removed[name]);

export const getRemovedAgentSettingNames = (
  removed: RemovedAgentSettings,
): RemovedAgentSettingName[] => REMOVED_AGENT_SETTING_ORDER.filter((name) => removed[name]);

// Translated when a message is shown, so the names follow the current language
export const REMOVED_AGENT_SETTING_NAME_KEYS: Record<RemovedAgentSettingName, string> = {
  voice: 'adminMainPage.campaigns.validation.removedSettingNames.voice',
  transcriber: 'adminMainPage.campaigns.validation.removedSettingNames.transcriber',
  llmProvider: 'adminMainPage.campaigns.validation.removedSettingNames.llmProvider',
  llmModel: 'adminMainPage.campaigns.validation.removedSettingNames.llmModel',
};

export const getRemovedAgentSettingNameKeys = (removed: RemovedAgentSettings): string[] =>
  getRemovedAgentSettingNames(removed).map((name) => REMOVED_AGENT_SETTING_NAME_KEYS[name]);

/**
 * Message for actions that resend the agent's saved settings (tools save, set as default). The API
 * answers 404 for a removed setting, so these are blocked until the agent is edited.
 */
export const getRemovedSettingsActionMessage = (
  removed: RemovedAgentSettings,
  t: (key: string, params?: Record<string, string>) => string,
): string | null => {
  const nameKeys = getRemovedAgentSettingNameKeys(removed);
  if (!nameKeys.length) return null;

  return t('adminMainPage.agents.removedSettingsReplaceFirst', {
    settings: formatList(
      nameKeys.map((key) => t(key)),
      t,
    ),
  });
};
